// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { MembershipReportData } from '@/membership/api/membership-types';
import { ChartAxis } from '@/membership/components/chart-axis';
import { ChartCursorTooltip } from '@/membership/components/chart-cursor-tooltip';
import { ChartDateAxis } from '@/membership/components/chart-date-axis';
import { ChartHoveredDay } from '@/membership/components/chart-hovered-day';
import {
  PlotLayout,
  toAxisScale,
  toDayAtOffset,
  toDayBand,
} from '@/membership/utilities/chart-geometry';
import {
  MembershipRollforward,
  SkuDelta,
  toDayDate,
  toRollforward,
} from '@/membership/utilities/membership-days';
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { MouseEvent, memo, useMemo, useState } from 'react';

// Half the chart's height carries each direction, so it aims at fewer gaps than
// the count chart above it.
const AXIS_TARGET_INTERVALS = 2;

// The bars are plain elements with inline styles rather than MUI ones. A year
// of them is thousands of nodes, and putting an sx prop on each cost more in
// Emotion serialising styles than the whole render did.
const BAR_OPACITY = 0.75;

// Zero sits halfway down, with the gains tinted above it and the losses below.
const ZERO_POSITION = 50;
const REGION_TINT = 0.1;

interface Props {
  // Already narrowed to the window and to the SKUs the chips leave showing, so
  // the chart answers the same question as the one above it.
  deltas: SkuDelta[];
  // The whole report, the SKUs still showing and where the window opens. The
  // tooltip reconciles a day against the one before it, which is a question the
  // windowed deltas cannot answer on their own.
  report: MembershipReportData;
  itemSkus: string[];
  windowStart: number;
  colors: Record<string, string>;
  height: number;
  startOn: DateTime;
  endOn: DateTime;
  onSelectDay: (dayIndex: number) => void;
  // The hovered day is shared with the chart above, so the two light up
  // together.
  hoveredDay: number | null;
  onHoverDay: (dayIndex: number | null) => void;
  layout: PlotLayout;
}

interface DeltaHover {
  x: number;
  y: number;
  day: number;
}

/**
 * What moved each day, joins above the zero line and departures below it.
 *
 * A day nothing moved is flat, which is most days of most projects. A unit that
 * moves between two of the project's own locations is a departure and a join on
 * the same day, so it shows as a bar each way rather than as no movement.
 */
export function MembershipDeltaChart(props: Props) {
  const theme = useTheme();
  const [hover, setHover] = useState<DeltaHover | null>(null);

  const dayCount = props.deltas.at(0)?.joined.length ?? 0;
  // Both halves share a scale, so a day that gained two and lost two reads as
  // balanced rather than as two bars of different heights. All of it is
  // memoised because it sits in the path of every mouse move.
  const scale = useMemo(() => {
    const joined = toTotals(props.deltas, dayCount, 'joined');
    const left = toTotals(props.deltas, dayCount, 'left');
    return toAxisScale(Math.max(0, ...joined, ...left), AXIS_TARGET_INTERVALS);
  }, [props.deltas, dayCount]);
  const labels = toMirroredLabels(scale.ticks);

  // Reconciling a day walks every membership in the report, which is far too
  // costly to redo on each pixel of travel, so it follows the day under the
  // cursor rather than the cursor itself.
  const hoveredDay = hover?.day ?? null;
  const rollforward = useMemo(
    () =>
      hoveredDay === null
        ? null
        : toRollforward(
            props.report,
            props.windowStart + hoveredDay,
            props.itemSkus
          ),
    [props.report, props.windowStart, props.itemSkus, hoveredDay]
  );

  const toDay = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return toDayAtOffset(
      event.clientX - bounds.left,
      bounds.width,
      props.layout,
      dayCount
    );
  };

  return (
    <>
      <Stack>
        <Stack direction="row" sx={{ height: props.height }}>
          <ChartAxis labels={labels} />

          <Box
            data-testid="membership-delta-chart"
            sx={{
              width: `${String(props.layout.plotWidth)}px`,
              flexShrink: 0,
              height: '100%',
              display: 'flex',
              cursor: 'pointer',
              position: 'relative',
            }}
            onMouseMove={(event) => {
              const day = toDay(event);
              if (day === null) return;
              props.onHoverDay(day);
              setHover({ x: event.clientX, y: event.clientY, day });
            }}
            onMouseLeave={() => {
              props.onHoverDay(null);
              setHover(null);
            }}
            onClick={(event) => {
              const day = toDay(event);
              if (day !== null) props.onSelectDay(day);
            }}
          >
            {/* Which half of the chart a bar is in is the whole reading, so the
              halves are tinted rather than left to the reader to work out from
              the zero line. These come first and the bars are positioned, so
              the tint stays behind them. */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                height: '50%',
                backgroundColor: alpha(theme.palette.success.main, REGION_TINT),
                pointerEvents: 'none',
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: '50%',
                backgroundColor: alpha(theme.palette.error.main, REGION_TINT),
                pointerEvents: 'none',
              }}
            />

            <ChartHoveredDay
              day={props.hoveredDay}
              layout={props.layout}
              dayCount={dayCount}
            />

            {/* Before the bars, so every line reads behind them. Zero is the
              one a bar is measured against, so it is drawn solid while the rest
              stay faint. */}
            {toGridPositions(scale.ticks, scale.max).map((position) => (
              <Box
                key={position}
                aria-hidden
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  // The last line would render a pixel under the plot, so it
                  // hangs off the bottom rather than off the top.
                  ...(position < 100
                    ? { top: `${String(position)}%` }
                    : { bottom: 0 }),
                  borderTop: 1,
                  borderColor:
                    position === ZERO_POSITION ? 'text.primary' : 'divider',
                  pointerEvents: 'none',
                }}
              />
            ))}

            <DeltaBars
              deltas={props.deltas}
              colors={props.colors}
              axisMax={scale.max}
              layout={props.layout}
            />
          </Box>

          <ChartAxis labels={labels} isRight />
        </Stack>

        <ChartDateAxis
          layout={props.layout}
          startOn={props.startOn}
          endOn={props.endOn}
        />
      </Stack>

      {hover !== null && rollforward !== null && (
        <DeltaTooltip
          hover={hover}
          deltas={props.deltas}
          colors={props.colors}
          rollforward={rollforward}
          startOn={props.startOn}
        />
      )}
    </>
  );
}

interface DeltaBarsProps {
  deltas: SkuDelta[];
  colors: Record<string, string>;
  axisMax: number;
  layout: PlotLayout;
}

/**
 * The movement bars, one column per day.
 *
 * Nothing here reads the hovered day, so a year of columns is skipped while the
 * cursor moves and only the tint behind them is redrawn.
 */
const DeltaBars = memo(function DeltaBars(props: DeltaBarsProps) {
  const dayCount = props.deltas.at(0)?.joined.length ?? 0;

  return (
    <>
      {Array.from({ length: dayCount }, (_unused, day) => (
        <div
          key={day}
          style={{
            width: `${String(toDayBand(day, props.layout, dayCount).width)}px`,
            marginRight: `${String(
              day === dayCount - 1 ? 0 : props.layout.gapWidth
            )}px`,
            flexShrink: 0,
            height: '100%',
            // Positioned, so the tinted halves and the hovered day stay behind.
            position: 'relative',
          }}
        >
          <DeltaHalf
            deltas={props.deltas}
            colors={props.colors}
            day={day}
            axisMax={props.axisMax}
            isJoined
          />
          <DeltaHalf
            deltas={props.deltas}
            colors={props.colors}
            day={day}
            axisMax={props.axisMax}
            isJoined={false}
          />
        </div>
      ))}
    </>
  );
});

interface DeltaHalfProps {
  deltas: SkuDelta[];
  colors: Record<string, string>;
  day: number;
  axisMax: number;
  isJoined: boolean;
}

/**
 * One day's movement in one direction, stacked by SKU against the zero line.
 * The departures are drawn in reverse so the two halves mirror each other and a
 * SKU keeps the same place in both stacks.
 */
function DeltaHalf(props: DeltaHalfProps) {
  const stacked = props.isJoined ? props.deltas : [...props.deltas].reverse();

  return (
    <div
      style={{
        height: '50%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: props.isJoined ? 'flex-end' : 'flex-start',
      }}
    >
      {stacked.map((delta) => (
        <div
          key={delta.itemSku}
          style={{
            height: `${String(
              ((props.isJoined
                ? delta.joined[props.day]
                : delta.left[props.day]) /
                props.axisMax) *
                100
            )}%`,
            backgroundColor: props.colors[delta.itemSku],
            opacity: BAR_OPACITY,
          }}
        />
      ))}
    </div>
  );
}

interface DeltaTooltipProps {
  hover: DeltaHover;
  deltas: SkuDelta[];
  colors: Record<string, string>;
  rollforward: MembershipRollforward;
  startOn: DateTime;
}

function DeltaTooltip(props: DeltaTooltipProps) {
  const moved = props.deltas.filter(
    (delta) =>
      delta.joined[props.hover.day] > 0 || delta.left[props.hover.day] > 0
  );

  return (
    <ChartCursorTooltip
      x={props.hover.x}
      y={props.hover.y}
      testId="membership-delta-tooltip"
    >
      <Typography
        variant="subtitle2"
        sx={{ pb: 0.5, borderBottom: 1, borderColor: 'divider' }}
      >
        {toDayDate(props.startOn, props.hover.day).toLocaleString(
          DateTime.DATE_MED
        )}
      </Typography>
      {/* The rollforward leads, because it is what the day amounts to. Its
          four figures reconcile, so the movement can be checked against the
          days either side rather than taken on trust. */}
      <Stack gap={0.25} sx={{ mt: 0.5 }}>
        <TooltipFigure
          label="Opening"
          value={String(props.rollforward.starting)}
        />
        <TooltipFigure
          label="+Joined"
          value={String(props.rollforward.joined)}
        />
        <TooltipFigure
          label="-Left"
          value={`(${String(props.rollforward.left)})`}
        />
        <TooltipFigure
          label="=Ending"
          value={String(props.rollforward.ending)}
          isTotal
        />
      </Stack>

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        By SKU
      </Typography>
      {moved.length === 0 ? (
        <Typography variant="caption" sx={{ display: 'block' }}>
          No movement
        </Typography>
      ) : (
        <Stack gap={0.25}>
          {moved.map((delta) => (
            <Stack
              key={delta.itemSku}
              direction="row"
              alignItems="center"
              gap={0.75}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: props.colors[delta.itemSku],
                }}
              />
              <Typography variant="caption" sx={{ flexGrow: 1 }}>
                {delta.itemSku}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {toMovement(
                  delta.joined[props.hover.day],
                  delta.left[props.hover.day]
                )}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </ChartCursorTooltip>
  );
}

// Both directions on one line, because a location change is a departure and a
// join at once and showing only the net would read as nothing having happened.
function toMovement(joined: number, left: number): string {
  const parts: string[] = [];
  if (joined > 0) parts.push(`+${String(joined)}`);
  if (left > 0) parts.push(`-${String(left)}`);
  return parts.join(' / ');
}

function toTotals(
  deltas: SkuDelta[],
  dayCount: number,
  key: 'joined' | 'left'
): number[] {
  return Array.from({ length: dayCount }, (_unused, day) =>
    deltas.reduce((total, delta) => total + delta[key][day], 0)
  );
}

// Top down: the joins above zero, then zero, then the departures under it. Both
// halves read the same distance from zero, which is what lets a day that gained
// two and lost two be seen as balanced.
function toMirroredLabels(ticks: number[]): string[] {
  return [
    ...[...ticks]
      .reverse()
      .slice(0, -1)
      .map((tick) => `+${String(tick)}`),
    '0',
    ...ticks.slice(1).map((tick) => `-${String(tick)}`),
  ];
}

// Where each label sits down the plot, as a percentage. Zero is halfway, and
// every other tick is mirrored either side of it.
function toGridPositions(ticks: number[], axisMax: number): number[] {
  const above = ticks.slice(1);
  return [
    ...[...above].reverse().map((tick) => 50 - (tick / axisMax) * 50),
    50,
    ...above.map((tick) => 50 + (tick / axisMax) * 50),
  ];
}

function TooltipFigure(props: {
  label: string;
  value: string;
  isTotal?: boolean;
}) {
  return (
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Typography variant="caption" color="text.secondary">
        {props.label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: props.isTotal === true ? 600 : 400,
        }}
      >
        {props.value}
      </Typography>
    </Stack>
  );
}
